/**
 * Climate Investment Pack — Public Deal Activation Page
 * Route: /verify/pack/:packId  |  No authentication required
 * Purpose: Convert FI viewing into structured deal engagement
 */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Shield, Download, FileText, Check, CheckCircle2, AlertCircle,
  AlertTriangle, Info, ChevronDown, ChevronRight, TrendingDown,
  Clock, DollarSign, Zap, Sparkles, Target, BarChart2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL    = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Utilities ─────────────────────────────────────────────────────────────
const fmt    = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const fmtK   = (n) => !n ? "$0" : n>=1e6 ? `$${(n/1e6).toFixed(1)}M` : n>=1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;
const capFst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g," ") : "";

const ACTION_LABELS = {
  solar_pv:"Solar PV", energy_efficiency:"Energy Efficiency", fuel_switch:"Fuel Switch",
  fleet_electrification:"Fleet Electrification", waste_reduction:"Waste Reduction",
  materials_optimization:"Low-Carbon Materials",
};
const VERIF_STEPS = [
  { key:"self_reported",       label:"Self-Reported",      desc:"Submitted by org" },
  { key:"system_validated",    label:"System Validated",   desc:"Automated checks" },
  { key:"analyst_reviewed",    label:"Analyst Reviewed",   desc:"DeCarbonIQ review" },
  { key:"third_party_verified",label:"Third-Party Verified",desc:"Independent audit" },
];

// ── Rule-based computations ────────────────────────────────────────────────
const BENCHMARKS = {
  solar_pv:              { range:[3,6],  roi:[50,200], label:"Africa Solar SME" },
  energy_efficiency:     { range:[2,5],  roi:[20,50],  label:"Commercial Efficiency" },
  fuel_switch:           { range:[2,5],  roi:[15,40],  label:"Fuel Transition" },
  fleet_electrification: { range:[4,8],  roi:[12,30],  label:"SME Fleet EV" },
  waste_reduction:       { range:[3,7],  roi:[10,35],  label:"Waste Management" },
  materials_optimization:{ range:[2,4],  roi:[10,15],  label:"Green Procurement" },
};

function classifyDeal(pack) {
  const actions  = pack.investment_case?.top_actions || [];
  const capex    = pack.investment_case?.total_capex_usd || 0;
  const conf     = pack.confidence_layer?.overall_score || 0;
  const verif    = pack.confidence_layer?.verification_status || "self_reported";
  const topType  = actions[0]?.action_type || "";
  const typeMap  = { solar_pv:"Solar Transition", energy_efficiency:"Energy Efficiency",
                     fuel_switch:"Fuel Transition", fleet_electrification:"Fleet Electrification",
                     waste_reduction:"Circular Economy", materials_optimization:"Low-Carbon Supply Chain" };
  return {
    dealType: typeMap[topType] || "Climate Action",
    ticket:   capex < 25000 ? "Micro ($0–$25k)" : capex < 100000 ? "Small ($25k–$100k)" :
              capex < 500000 ? "Medium ($100k–$500k)" : "Large ($500k+)",
    risk:     conf >= 75 && ["system_validated","analyst_reviewed","third_party_verified"].includes(verif) ? "Low-Medium" :
              conf >= 60 ? "Medium" : conf >= 40 ? "Medium-High" : "High",
    topType,
  };
}

function getBenchmark(pack) {
  const actions = pack.investment_case?.top_actions || [];
  const topType = actions[0]?.action_type || "";
  const bm      = BENCHMARKS[topType] || { range:[3,7], roi:[10,40], label:"SME Climate Finance" };
  const payback = pack.investment_case?.weighted_payback_years || 0;
  const roi     = pack.investment_case?.weighted_roi_pct || 0;
  const paybackOk   = payback >= bm.range[0] && payback <= bm.range[1];
  const paybackFast = payback < bm.range[0] && payback > 0;
  return {
    label: bm.label, paybackRange: bm.range, roiRange: bm.roi,
    paybackStatus: paybackFast ? "faster" : paybackOk ? "within_range" : "slower",
    paybackMsg: paybackFast
      ? `${fmt(payback,1)} yrs — faster than typical range (${bm.range[0]}–${bm.range[1]} yrs). Above-average performance.`
      : paybackOk
      ? `${fmt(payback,1)} yrs — within typical range (${bm.range[0]}–${bm.range[1]} yrs). Acceptable.`
      : `${fmt(payback,1)} yrs — exceeds typical range (${bm.range[0]}–${bm.range[1]} yrs). Review warranted.`,
    roiMsg: `${fmt(roi,0)}% vs typical ${bm.roi[0]}–${bm.roi[1]}% for ${bm.label}.`,
  };
}

function getUrgency(pack) {
  const grade   = pack.investment_case?.investment_grade || "moderate";
  const topType = pack.investment_case?.top_actions?.[0]?.action_type || "";
  const label   = ACTION_LABELS[topType] || capFst(topType);
  const signals = {
    strong:   `High priority — fast payback driven by ${label}. Engage within 7–14 days while assumptions are current.`,
    good:     `Strong case — payback within optimal range. Recommend review within 14 days.`,
    moderate: "Moderate opportunity. Initial engagement recommended within 30 days.",
    marginal: "Extended due diligence recommended. No immediate time pressure.",
  };
  return { message: signals[grade] || signals.moderate,
           timeline: grade==="strong"?"7–14 days":grade==="good"?"14 days":grade==="moderate"?"30 days":"60+ days",
           validity: "Financial projections remain valid for 90 days from generation." };
}

function deriveRisks(pack) {
  const risks = [];
  const conf  = pack.confidence_layer?.overall_score || 0;
  const tier  = pack.confidence_layer?.tier || "low";
  const verif = pack.confidence_layer?.verification_status || "self_reported";
  const evCov = pack.evidence_summary?.coverage_pct || 0;
  const pb    = pack.investment_case?.weighted_payback_years || 0;
  const acts  = pack.investment_case?.top_actions || [];
  if (conf < 60) risks.push({ level:"high", text:`Data confidence is ${capFst(tier)} (${Math.round(conf)}/100). Independent verification recommended before investment commitment.` });
  if (evCov < 50) risks.push({ level:"medium", text:`Evidence coverage at ${evCov.toFixed(0)}%: less than half of activities have verified supporting documents.` });
  if (verif === "self_reported") risks.push({ level:"medium", text:"Emissions data is self-reported and has not undergone system-level or third-party validation." });
  if (pb > 7) risks.push({ level:"low", text:`Portfolio payback of ${fmt(pb,1)} yrs exceeds the typical green finance threshold of 5–7 years. Assess project-level cases individually.` });
  const lowConf = acts.filter(a => a.confidence_level === "low");
  if (lowConf.length > 0) risks.push({ level:"low", text:`${lowConf.length} action(s) have low-confidence estimates. Verify site-specific data before committing.` });
  return risks;
}

// ── Section Components ─────────────────────────────────────────────────────
function TrustHeader({ pack }) {
  const conf   = pack.confidence_layer?.overall_score || 0;
  const tier   = pack.confidence_layer?.tier || "unknown";
  const verif  = pack.confidence_layer?.verification_status || "self_reported";
  const grade  = pack.investment_case?.investment_grade || "moderate";
  const genDate= (pack.generated_at || pack.pack_metadata?.generated_at || "")?.slice(0,10);
  const { dealType, ticket, risk } = classifyDeal(pack);
  const urgency = getUrgency(pack);

  const confColor = conf>=75?"text-emerald-400":conf>=50?"text-blue-400":conf>=40?"text-amber-400":"text-red-400";
  const gradeConf = { strong:{bg:"bg-emerald-600",label:"STRONG INVESTMENT GRADE"}, good:{bg:"bg-blue-600",label:"GOOD INVESTMENT GRADE"},
                      moderate:{bg:"bg-amber-600",label:"MODERATE INVESTMENT GRADE"}, marginal:{bg:"bg-red-600",label:"MARGINAL"} };
  const gc = gradeConf[grade] || gradeConf.moderate;
  const riskColors = { "Low-Medium":"bg-emerald-900/40 text-emerald-300", "Medium":"bg-blue-900/40 text-blue-300",
                       "Medium-High":"bg-amber-900/40 text-amber-300", "High":"bg-red-900/40 text-red-300" };

  return (
    <div className="bg-[#0F172A] pt-16 pb-8 px-6">
        {/* Grade badge */}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white mb-3 ${gc.bg}`}>
          {gc.label}
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-1">
          Climate Investment Opportunity
        </h1>
        <p className="text-slate-400 text-sm mb-5">
          {pack.identity?.org_name || "—"} · {pack.identity?.reporting_year ? `FY${pack.identity.reporting_year}` : ""} · {pack.identity?.country || ""}
        </p>

        {/* Deal Classification Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="deal-classification">
          {[
            { label:"Deal Type",    value: dealType,       icon: Target   },
            { label:"Ticket Size",  value: ticket,          icon: DollarSign },
            { label:"Risk Profile", value: risk,            icon: Shield   },
            { label:"Engagement",   value: urgency.timeline,icon: Clock    },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
              <Icon size={13} className="text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide leading-none">{label}</p>
                <p className="text-xs font-semibold text-white mt-0.5">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Urgency Signal */}
        <div className="flex items-start gap-2 px-4 py-3 bg-emerald-900/30 border border-emerald-700/40 rounded-xl mb-4" data-testid="urgency-signal">
          <Sparkles size={13} className="text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-emerald-300 mb-0.5">Investment Signal</p>
            <p className="text-xs text-emerald-200">{urgency.message}</p>
            <p className="text-[10px] text-emerald-500 mt-1">{urgency.validity}</p>
          </div>
        </div>

        {/* Trust Signal Pills */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl" data-testid="trust-confidence">
            <Shield size={13} className={confColor} />
            <span className={`text-sm font-bold tabular-nums ${confColor}`}>{fmt(conf,0)}</span>
            <span className="text-slate-400 text-xs">/ 100 · {capFst(tier)}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl" data-testid="trust-verif">
            {["analyst_reviewed","third_party_verified"].includes(verif)
              ? <CheckCircle2 size={13} className="text-emerald-400" />
              : <AlertCircle size={13} className="text-amber-400" />}
            <span className="text-slate-300 text-xs">{capFst(verif.replace(/_/g," "))}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl">
            <span className="text-slate-400 text-xs font-mono">{pack.pack_id}</span>
          </div>
          {genDate && <span className="text-slate-500 text-xs">Generated {genDate}</span>}
        </div>
      </div>
  );
}

function CompanySnapshot({ pack }) {
  const id = pack.identity || {};
  return (
    <section data-testid="company-snapshot">
      <SectionLabel title="Company Profile" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          ["Organization", id.org_name || "—"],
          ["Sector",       id.sector   || "—"],
          ["Location",     [id.country, id.geography?.toUpperCase()].filter(Boolean).join(" · ") || "—"],
          ["Org Type",     (id.org_type||"sme").toUpperCase()],
          ["Reporting Year", id.reporting_year ? `FY${id.reporting_year}` : "—"],
          ["Employees",    id.employee_count || "—"],
        ].map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{label}</p>
            <p className="text-sm font-semibold text-slate-900">{String(value)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function InvestmentCase({ pack }) {
  const invest  = pack.investment_case || {};
  const climate = pack.climate_profile || {};
  const hotspot = (climate.hotspots || [])[0];
  const s1 = climate.scope_breakdown?.scope_1?.tco2e || 0;
  const s2 = climate.scope_breakdown?.scope_2?.tco2e || 0;
  const total = climate.total_tco2e || 0;
  const grade = invest.investment_grade || "moderate";
  const bm = getBenchmark(pack);
  const bmStatus = bm.paybackStatus;
  const bmColor = bmStatus==="faster"?"text-emerald-600":bmStatus==="within_range"?"text-blue-600":"text-amber-600";
  const bmBg    = bmStatus==="faster"?"bg-emerald-50 border-emerald-200":bmStatus==="within_range"?"bg-blue-50 border-blue-200":"bg-amber-50 border-amber-200";

  const keySignals = {
    strong:   `Compelling opportunity: ${fmtK(invest.total_annual_savings_usd)}/yr savings from ${fmtK(invest.total_capex_usd)} investment. ${fmt(invest.weighted_payback_years,1)}-year payback. ${fmt(invest.total_co2_reduction_tco2e_yr,1)} tCO₂e/yr reduction.`,
    good:     `Viable case: ${fmt(invest.weighted_payback_years,1)}-year payback on ${fmtK(invest.total_capex_usd)} capex. ${fmtK(invest.total_annual_savings_usd)}/yr savings and ${fmt(invest.total_co2_reduction_tco2e_yr,1)} tCO₂e/yr reduction.`,
    moderate: `Moderate returns: ${fmtK(invest.total_capex_usd)} investment → ${fmtK(invest.total_annual_savings_usd)}/yr savings (${fmt(invest.weighted_payback_years,1)} yr payback). Data strengthening could improve the case.`,
    marginal: `Investment case requires review. Recommend data enhancement before financing decision.`,
  };

  return (
    <section data-testid="investment-case">
      <SectionLabel title="Investment Case" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[
              { label:"Total CapEx",    value: fmtK(invest.total_capex_usd),                color:"text-[#0F172A]" },
              { label:"Annual Savings", value: `${fmtK(invest.total_annual_savings_usd)}/yr`,color:"text-[#059669]" },
              { label:"CO₂ Reduction",  value: `${fmt(invest.total_co2_reduction_tco2e_yr,1)} t/yr`, color:"text-blue-600" },
              { label:"Payback Period", value: `${fmt(invest.weighted_payback_years,1)} yrs`, color:"text-amber-600" },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-[#F8FAFC] border border-slate-100 rounded-xl p-4">
                <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
                <p className="text-xs text-slate-400 uppercase tracking-wide mt-1">{label}</p>
              </div>
            ))}
          </div>
          {/* Benchmark context */}
          <div className={`border rounded-xl p-3 ${bmBg}`} data-testid="benchmark-context">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Benchmark: {bm.label}</p>
            <div className="space-y-1">
              <div className="flex items-start gap-1.5 text-xs">
                <span className={`font-medium ${bmColor}`}>Payback:</span>
                <span className="text-slate-600">{bm.paybackMsg}</span>
              </div>
              <div className="flex items-start gap-1.5 text-xs">
                <span className="font-medium text-slate-500">ROI:</span>
                <span className="text-slate-500">{bm.roiMsg}</span>
              </div>
            </div>
          </div>
          {/* Key signal */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Key Investment Signal</p>
            <p className="text-sm text-[#0F172A] leading-relaxed font-medium" data-testid="key-signal">
              {keySignals[grade] || keySignals.moderate}
            </p>
          </div>
        </div>
        {/* Emissions overview */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1.5">Total Emissions</p>
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-[#0F172A] tabular-nums" data-testid="total-emissions">{fmt(total,1)}</span>
              <span className="text-base text-slate-400 mb-1">tCO₂e</span>
            </div>
          </div>
          <div className="space-y-2">
            {[["Scope 1",s1,"#0F172A"],["Scope 2",s2,"#059669"]].filter(([,v])=>v>0).map(([lbl,val,color])=>(
              <div key={lbl}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">{lbl}</span>
                  <span className="font-bold tabular-nums" style={{color}}>{fmt(val,1)} t · {fmt(val/(total||1)*100,0)}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{width:`${val/(total||1)*100}%`,backgroundColor:color}} />
                </div>
              </div>
            ))}
          </div>
          {hotspot && (
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-xs text-slate-400 mb-0.5">Top Emission Driver</p>
              <p className="text-sm font-semibold text-slate-900">{hotspot.category_label || capFst(hotspot.category)}</p>
              <p className="text-xs text-slate-400 tabular-nums">
                {fmt(hotspot.tco2e,2)} tCO₂e · {fmt(hotspot.pct_total ?? (hotspot.tco2e/(total||1)*100),0)}% of total
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function SelectedActions({ pack }) {
  const [openId, setOpenId] = useState(null);
  const actions = pack.investment_case?.top_actions || [];
  if (!actions.length) return null;

  return (
    <section data-testid="selected-actions">
      <SectionLabel title={`Selected Actions (${actions.length})`} />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {actions.map((action, i) => {
          const label   = ACTION_LABELS[action.action_type] || capFst(action.action_type);
          const isOpen  = openId === i;
          const confCls = {high:"text-emerald-600 bg-emerald-50",medium:"text-amber-600 bg-amber-50",low:"text-red-600 bg-red-50"}[action.confidence_level||"medium"];
          const notes   = (action.derivation_notes||"").split("Assumptions:")[0].trim();
          return (
            <div key={i} className={i>0?"border-t border-slate-100":""}>
              <button onClick={()=>setOpenId(isOpen?null:i)} data-testid={`action-row-${i}`}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left">
                <div className="w-6 h-6 bg-[#0F172A] text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">{i+1}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{label}</p>
                  <p className="text-xs text-slate-400 truncate">{action.description}</p>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                  <span><span className="font-bold text-[#059669]">{fmt(action.co2_reduction,1)}t</span> CO₂/yr</span>
                  <span><span className="font-bold text-[#0F172A]">{fmtK(action.capex_required)}</span> capex</span>
                  <span><span className="font-bold text-amber-600">{fmt(action.payback_years,1)}yr</span> payback</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${confCls}`}>{action.confidence_level}</span>
                </div>
                {isOpen?<ChevronDown size={14} className="text-slate-400 flex-shrink-0"/>:<ChevronRight size={14} className="text-slate-400 flex-shrink-0"/>}
              </button>
              {isOpen && (
                <div className="px-5 pb-5 bg-slate-50 border-t border-slate-100">
                  <div className="grid grid-cols-3 gap-2 mt-3 mb-3">
                    {[["CapEx",fmtK(action.capex_required)],["Savings",`${fmtK(action.annual_savings)}/yr`],["CO₂",`${fmt(action.co2_reduction,1)} t/yr`],
                      ["Payback",`${fmt(action.payback_years,1)} yrs`],["ROI",`${fmt(action.roi_pct,0)}%`],["Priority",`${fmt(action.priority_score,0)}/100`]].map(([lbl,val])=>(
                      <div key={lbl} className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
                        <p className="text-sm font-semibold text-slate-900 tabular-nums">{val}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{lbl}</p>
                      </div>
                    ))}
                  </div>
                  {notes && (
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <p className="text-xs font-medium text-slate-500 mb-1">Assumption basis</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{notes.slice(0,300)}</p>
                      <p className="text-[10px] text-slate-400 mt-1 italic">Sources: IRENA 2023 · IEA 2024 · SEAI/UNEP · Ellen MacArthur Foundation</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RiskAndDocuments({ pack }) {
  const conf   = pack.confidence_layer?.overall_score || 0;
  const tier   = pack.confidence_layer?.tier || "low";
  const risks  = deriveRisks(pack);
  const hash   = pack.climate_profile?.calculation_hash;
  const meth   = pack.climate_profile?.methodology || "GHG Protocol v2.0";
  const confColor = conf>=75?"#059669":conf>=50?"#3B82F6":conf>=40?"#D97706":"#DC2626";
  const riskCls = { high:"bg-red-50 border-red-200 text-red-700", medium:"bg-amber-50 border-amber-200 text-amber-700", low:"bg-slate-50 border-slate-200 text-slate-600" };
  const riskIcon = { high:<AlertTriangle size={12}/>, medium:<AlertCircle size={12}/>, low:<Info size={12}/> };

  const downloadMemo = () => {
    const a = document.createElement("a");
    a.href = `${BACKEND_URL}/api/public/packs/${pack.pack_id}/memo.pdf`;
    a.download = `${pack.pack_id}-investment-memo.pdf`; a.click();
  };

  const bars = [
    {label:"Data Completeness",key:"completeness",weight:"25%"},
    {label:"Evidence Coverage",key:"evidence_score",weight:"25%"},
    {label:"Consistency",key:"consistency",weight:"20%"},
    {label:"Source Reliability",key:"source_reliability",weight:"15%"},
    {label:"Benchmark Fit",key:"benchmark_fit",weight:"15%"},
  ];

  return (
    <section data-testid="risk-documents">
      <SectionLabel title="Risk & Credibility" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0" style={{width:80,height:80}}>
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="30" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                <circle cx="40" cy="40" r="30" fill="none" stroke={confColor} strokeWidth="8"
                  strokeDasharray={`${(conf/100)*188.5} 188.5`} strokeDashoffset="47.1" strokeLinecap="round" />
                <text x="40" y="36" textAnchor="middle" fill="#0F172A" fontSize="15" fontWeight="700">{Math.round(conf)}</text>
                <text x="40" y="50" textAnchor="middle" fill="#94A3B8" fontSize="9">/ 100</text>
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 capitalize" data-testid="confidence-tier">{capFst(tier)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{pack.confidence_layer?.tier_label || "—"}</p>
              <p className="text-xs text-slate-400 mt-1">
                Evidence: <strong className="text-[#0F172A]">{pack.evidence_summary?.verified_count||0}/{pack.evidence_summary?.total_evidence||0}</strong> docs verified
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {bars.map(({label,key,weight}) => {
              const val = pack.confidence_layer?.[key] || 0;
              const bc  = val>=75?"#059669":val>=50?"#3B82F6":val>=40?"#D97706":"#DC2626";
              return (
                <div key={key}>
                  <div className="flex justify-between mb-0.5">
                    <span className="text-xs text-slate-500">{label} <span className="text-slate-300">{weight}</span></span>
                    <span className="text-xs font-bold tabular-nums" style={{color:bc}}>{fmt(val,0)}%</span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{width:`${val}%`,backgroundColor:bc}} />
                  </div>
                </div>
              );
            })}
          </div>
          {risks.length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Key Risks</p>
              {risks.map((r,i) => (
                <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${riskCls[r.level]||riskCls.low}`}>
                  <span className="flex-shrink-0 mt-0.5">{riskIcon[r.level]}</span>
                  <span className="leading-relaxed">{r.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Documents</p>
            <button onClick={downloadMemo} data-testid="download-memo-btn"
              className="w-full flex items-center justify-between p-4 bg-[#0F172A] text-white rounded-xl hover:bg-slate-800 transition-colors mb-3">
              <div className="flex items-center gap-2">
                <Download size={16} />
                <div className="text-left">
                  <p className="text-sm font-semibold">Investment Memo PDF</p>
                  <p className="text-xs text-slate-400">2-page FI-ready summary</p>
                </div>
              </div>
              <FileText size={16} className="text-slate-400" />
            </button>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 mb-3">
              <p className="text-xs font-semibold text-slate-700">Methodology: {meth}</p>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Data Integrity</p>
            <div className="space-y-1.5">
              {hash && (
                <div className="flex items-start gap-2 text-xs">
                  <Check size={11} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div><p className="text-slate-500">Calculation fingerprint</p>
                    <p className="font-mono text-[#0F172A] text-[10px] break-all">{hash}</p></div>
                </div>
              )}
              {["GHG Protocol Corporate Standard v2.0","Emission factors locked at calculation time","IEA/IPCC/DEFRA approved sources"].map(line => (
                <div key={line} className="flex items-center gap-2 text-xs">
                  <Check size={11} className="text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-500">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Deal Activation Form (replaces old FiCta) ─────────────────────────────
function DealActivation({ packId }) {
  const [mode, setMode]       = useState("cta");    // cta | form | success
  const [formType, setFormType] = useState(null);   // "interest" | "review"
  const [form, setForm]       = useState({ name:"", institution:"", email:"", interest_level:"medium", notes:"" });
  const [submitting, setSub]  = useState(false);
  const [refId, setRefId]     = useState(null);
  const [error, setError]     = useState(null);

  const openForm = (type) => { setFormType(type); setMode("form"); setError(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.institution || !form.email) { setError("Name, institution, and email are required."); return; }
    setSub(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/packs/${packId}/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setRefId(data.reference_id);
      setMode("success");
    } catch (err) { setError(err.message); }
    setSub(false);
  };

  const interestLevels = [
    { value:"high",      label:"High — Active deal pipeline" },
    { value:"medium",    label:"Medium — Evaluating" },
    { value:"exploring", label:"Exploring — Research only" },
  ];

  return (
    <section data-testid="deal-activation">
      {mode === "cta" && (
        <div className="bg-[#0F172A] rounded-xl p-8">
          <div className="max-w-lg">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">For Financial Institutions</p>
            <h2 className="text-xl font-bold text-white mb-2">Ready to Engage?</h2>
            <p className="text-slate-400 text-sm mb-6">
              This investment opportunity has been pre-screened by DeCarbonIQ.
              All data is traceable, versioned, and available for due diligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => openForm("interest")} data-testid="cta-express-interest"
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 transition-colors">
                <Sparkles size={14} /> Express Interest
              </button>
              <button onClick={() => openForm("review")} data-testid="cta-start-review"
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 border border-slate-600 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors">
                <CheckCircle2 size={14} /> Start Review
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === "form" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="interest-form">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-bold text-[#0F172A]">
                {formType === "review" ? "Start Review Process" : "Express Interest"}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {formType === "review" ? "Begin a formal deal review." : "Register your interest — our team will be in touch within 24 hours."}
              </p>
            </div>
            <button onClick={() => setMode("cta")} className="text-xs text-slate-400 hover:text-slate-600">Cancel</button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { id:"name",        label:"Your Name *",      placeholder:"Jane Smith",          type:"text" },
                { id:"institution", label:"Institution *",    placeholder:"Ecobank Nigeria",      type:"text" },
                { id:"email",       label:"Email Address *",  placeholder:"jane@institution.com", type:"email" },
              ].map(({id,label,placeholder,type}) => (
                <div key={id} className={id==="email"?"sm:col-span-2":""}>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
                  <input type={type} value={form[id]} onChange={e=>setForm(f=>({...f,[id]:e.target.value}))}
                    placeholder={placeholder} required
                    className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#059669] focus:border-transparent" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Interest Level</label>
              <select value={form.interest_level} onChange={e=>setForm(f=>({...f,interest_level:e.target.value}))}
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                {interestLevels.map(({value,label}) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
              <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2}
                placeholder="Specific questions or context for your team..."
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#059669]" />
            </div>
            {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <button type="submit" disabled={submitting} data-testid="submit-interest-btn"
              className="w-full px-4 py-3 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {submitting ? "Submitting..." : formType === "review" ? "Submit Review Request" : "Submit Interest"}
            </button>
            <p className="text-xs text-slate-400 text-center">Your details are shared only with the DeCarbonIQ deal team.</p>
          </form>
        </div>
      )}

      {mode === "success" && (
        <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center" data-testid="interest-success">
          <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={28} className="text-[#059669]" />
          </div>
          <h3 className="text-lg font-bold text-[#0F172A] mb-2">Interest Registered</h3>
          <p className="text-sm text-slate-500 mb-3">Our deal team will be in touch within 24 hours.</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-xl border border-emerald-200">
            <span className="text-xs text-slate-500">Reference ID:</span>
            <span className="text-xs font-mono font-bold text-[#059669]">{refId}</span>
          </div>
          <p className="text-xs text-slate-400 mt-3">Keep this reference for your records.</p>
        </div>
      )}
    </section>
  );
}

function VerificationLadder({ pack }) {
  const verif = pack.confidence_layer?.verification_status || "self_reported";
  const currentIdx = VERIF_STEPS.findIndex(s => s.key === verif);
  const upgradeMsg = {
    self_reported:    "Submit for system validation to strengthen data credibility.",
    system_validated: "Request analyst review for higher institutional trust.",
    analyst_reviewed: "Engage an accredited third-party verifier for maximum credibility.",
    third_party_verified: "Maximum verification level achieved.",
  };

  return (
    <section data-testid="verification-ladder">
      <SectionLabel title="Verification Status" />
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-start relative mb-5">
          <div className="absolute top-3 left-3 right-3 h-0.5 bg-slate-200 z-0" />
          <div className="absolute top-3 left-3 h-0.5 z-0 bg-[#059669]"
            style={{width:`${currentIdx > 0 ? (currentIdx/(VERIF_STEPS.length-1))*100 : 0}%`}} />
          {VERIF_STEPS.map((step,i) => {
            const done = i <= currentIdx;
            const current = i === currentIdx;
            return (
              <div key={step.key} className="flex-1 flex flex-col items-center z-10">
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center ${done?"bg-[#059669] border-[#059669]":"bg-white border-slate-300"}`}>
                  {done && <Check size={13} className="text-white" strokeWidth={3} />}
                </div>
                <p className={`text-xs mt-2 font-medium text-center ${current?"text-[#059669]":done?"text-[#0F172A]":"text-slate-400"}`}>{step.label}</p>
                <p className={`text-[10px] mt-0.5 text-center ${done?"text-slate-400":"text-slate-300"}`}>{step.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
          <Info size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-500">{upgradeMsg[verif]}</p>
        </div>
      </div>
    </section>
  );
}

// ── Reusable section label ─────────────────────────────────────────────────
function SectionLabel({ title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-1 h-5 bg-[#059669] rounded-full" />
      <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">{title}</h2>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function PackVerify() {
  const { packId } = useParams();
  const [pack,    setPack]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/public/packs/${packId}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status===404?"Pack not found":"Failed to load pack");
        return r.json();
      })
      .then(d => { setPack(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [packId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-400 text-sm">Loading investment pack...</p>
      </div>
    </div>
  );

  if (error || !pack) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center max-w-sm p-8">
        <AlertTriangle size={32} className="mx-auto text-red-400 mb-3" />
        <h2 className="text-lg font-bold text-[#0F172A] mb-2">Pack Not Found</h2>
        <p className="text-sm text-slate-500 mb-4">{error || "This investment pack could not be found."}</p>
        <Link to="/" className="text-sm text-[#059669] hover:underline">← Return to DeCarbonIQ</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans" data-testid="pack-verify-page">
      {/* Sticky Nav */}
      <nav className="sticky top-0 z-40 bg-[#0F172A] border-b border-slate-800">
        <div className="px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{filter:"brightness(0) invert(1)"}} />
            <p className="text-[10px] text-slate-500 uppercase tracking-widest hidden sm:block">Climate Finance Platform</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 font-mono hidden md:block">{packId}</span>
            <Link to="/login" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-slate-300 text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors">
              Log In
            </Link>
          </div>
        </div>
      </nav>

      <TrustHeader pack={pack} />
      <div className="px-6 py-10 space-y-10">
        <CompanySnapshot  pack={pack} />
        {/* FRS on Public Pack — deal language for FIs */}
        {pack.frs && (
          <section data-testid="public-frs-section">
            <SectionLabel title="Financing Readiness" />
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-4xl font-bold tabular-nums" style={{color: pack.frs.color}}>
                      {Math.round(pack.frs.score || 0)}
                    </span>
                    <div>
                      <p className="text-sm font-bold" style={{color: pack.frs.color}}>{pack.frs.grade} · {pack.frs.band_label}</p>
                      <p className="text-xs text-slate-400">{pack.frs.probability?.label}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 mb-2">Component breakdown</p>
                  <div className="space-y-1 w-40">
                    {[["Financial",pack.frs.components?.financial?.score||0,40,"#0F172A"],
                      ["Data",     pack.frs.components?.data_credibility?.score||0,25,"#3B82F6"],
                      ["Verif.",   pack.frs.components?.verification?.score||0,15,"#6366F1"],
                      ["Feasibility",pack.frs.components?.feasibility?.score||0,10,"#F59E0B"],
                      ["Market",   pack.frs.components?.market_alignment?.score||0,10,"#10B981"],
                    ].map(([label,score,max,color]) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 w-14">{label}</span>
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{width:`${(score/max)*100}%`,backgroundColor:color}} />
                        </div>
                        <span className="text-[10px] font-bold tabular-nums w-8 text-right" style={{color}}>{score}/{max}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {pack.frs.distance_to_target && !pack.frs.distance_to_target.at_target && (
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <p className="text-xs text-slate-500">
                    <strong className="text-slate-700">Gap to Investment Grade:</strong>{" "}
                    +{pack.frs.distance_to_target.gap} points needed (current: {pack.frs.distance_to_target.current}, target: 70+)
                  </p>
                </div>
              )}
              <p className="text-[10px] text-slate-400 italic mt-3">{pack.frs.methodology}</p>
            </div>
          </section>
        )}
        <InvestmentCase   pack={pack} />
        <SelectedActions  pack={pack} />
        <RiskAndDocuments pack={pack} />
        <DealActivation   packId={packId} />
        <VerificationLadder pack={pack} />
      </div>

      <footer className="border-t border-slate-200 bg-white mt-10">
        <div className="px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="DeCarbonIQ" className="h-5 w-auto opacity-50" style={{filter:"grayscale(1)"}} />
            <p className="text-xs text-slate-400">© {new Date().getFullYear()} DeCarbonIQ. All rights reserved.</p>
          </div>
          <p className="text-xs text-slate-400 text-center">
            For informational purposes only. Not investment advice. All estimates rule-based.
          </p>
        </div>
      </footer>
    </div>
  );
}
